import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface ColumnDef {
  id: string;
  label: string;
  /** Shown unless the admin has chosen otherwise. Defaults to true. */
  defaultVisible?: boolean;
  /** Always shown, and cannot be unticked. */
  locked?: boolean;
}

/**
 * Which columns of an admin table are shown, remembered in this browser (localStorage), so each
 * admin can choose without affecting anyone else.
 */
export function useColumnPrefs(storageKey: string, columns: ColumnDef[]) {
  const defaults = columns.filter((c) => c.locked || c.defaultVisible !== false).map((c) => c.id);
  const [visible, setVisible] = useState<string[]>(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (Array.isArray(saved)) return columns.filter((c) => c.locked || saved.includes(c.id)).map((c) => c.id);
      }
    } catch { /* storage unavailable or unreadable: use the defaults */ }
    return defaults;
  });

  const save = useCallback((next: string[]) => {
    setVisible(next);
    try { window.localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* not remembered */ }
  }, [storageKey]);

  return {
    isVisible: (id: string) => visible.includes(id),
    count: visible.length,
    toggle: (id: string) => save(columns.filter((c) => c.locked || (c.id === id ? !visible.includes(id) : visible.includes(c.id))).map((c) => c.id)),
    reset: () => save(defaults),
  };
}

/** A gear that opens a list of checkboxes, one per column. */
export const AdminColumnPicker: React.FC<{ columns: ColumnDef[]; prefs: ReturnType<typeof useColumnPrefs> }> = ({ columns, prefs }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div className="admin-colpicker" ref={ref}>
      <button
        type="button"
        className="admin-icon-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Choose columns"
        title="Choose columns"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>
      {open && (
        <div className="admin-colpicker-pop" role="dialog" aria-label="Columns">
          <div className="admin-colpicker-title">Columns</div>
          <ul className="admin-colpicker-list">
            {columns.map((c) => (
              <li key={c.id}>
                <label className="admin-colpicker-item">
                  <input type="checkbox" checked={prefs.isVisible(c.id)} disabled={c.locked} onChange={() => prefs.toggle(c.id)} />
                  <span>{c.label}</span>
                </label>
              </li>
            ))}
          </ul>
          <button type="button" className="admin-btn admin-btn-secondary admin-btn-compact" onClick={prefs.reset}>Reset to default</button>
        </div>
      )}
    </div>
  );
};
